import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Paper, Group, Text, Button, Badge, Table, Stack,
  TextInput, NumberInput, ActionIcon, Tooltip,
  Loader, Alert, Modal, Collapse, SimpleGrid, Card,
  Divider, Menu
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft, IconCheck, IconX, IconAlertCircle,
  IconCpu, IconTrash, IconEye, IconEyeOff,
  IconCurrencyFrank, IconFileDescription,
  IconFile, IconPaperclip, IconUpload, IconPlus
} from '@tabler/icons-react';
import { TYPE_ICONS, STATUS_COLORS, TAG_COLORS, TAG_OPTIONS } from './accounting-constants';
import apiService from '../services/api';
import { formatPrice } from '../utils/formatPrice';

const getEntryType = (item, docType) => {
  if (item.entry_type) return item.entry_type;
  const sign = (Number(item.total_amount) || 0) >= 0 ? 'positive' : 'negative';
  if (docType === 'receipt' || docType === 'credit_note' || docType === 'debit_note') return 'expense';
  if (docType === 'bank_statement') return sign === 'positive' ? 'income' : 'expense';
  return sign === 'positive' ? 'income' : 'expense';
};

const toDateInputValue = (v) => {
  if (!v) return '';
  if (v instanceof Date && !isNaN(v.getTime())) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const d = new Date(v);
    if (!isNaN(d.getTime())) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
  }
  return '';
};



export default function AdminAccountingDocument() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRaw, setShowRaw] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [uploadingReceipt, setUploadingReceipt] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveWarning, setApproveWarning] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [headerFields, setHeaderFields] = useState({});
  const [fileUrl, setFileUrl] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const objectUrlRef = useRef(null);

  const fetchDoc = useCallback(async () => {
    try {
      const data = await apiService.getAccountingDocument(id);
      setDoc(data);
      setItems(data.items || []);
      setHeaderFields({
        counterparty_name: data.counterparty_name || '',
        document_date: toDateInputValue(data.document_date),
        invoice_number: data.invoice_number || '',
        reference_number: data.reference_number || '',
        mwst_number: data.mwst_number || '',
        iban: data.iban || '',
        payment_reference: data.payment_reference || ''
      });
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDoc(); }, [fetchDoc]);

  useEffect(() => {
    if (!doc?.file_id) return;
    let cancelled = false;
    setFileLoading(true);
    setFileError(null);
    apiService.getFileBlob(doc.file_id).then(blob => {
      if (cancelled) return;
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      setFileUrl(url);
      setFileLoading(false);
    }).catch(err => {
      if (cancelled) return;
      setFileError(err.message);
      setFileLoading(false);
    });
    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, [doc?.file_id]);

  const handleProcess = async () => {
    setProcessing(true);
    try {
      await apiService.processAccountingDocument(id);
      notifications.show({ title: 'Processing', message: 'AI extraction started', color: 'blue' });
      setTimeout(fetchDoc, 2000);
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    } finally {
      setProcessing(false);
    }
  };

  const handleApprove = async () => {
    try {
      const result = await apiService.approveAccountingDocument(id);
      if (result.warning) {
        setApproveWarning(result);
        return;
      }
      notifications.show({ title: 'Approved', color: 'green' });
      fetchDoc();
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const handleForceApprove = async () => {
    setApproveWarning(null);
    try {
      await apiService.approveAccountingDocument(id, true);
      notifications.show({ title: 'Approved', color: 'green' });
      fetchDoc();
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const handleReject = async () => {
    try {
      await apiService.rejectAccountingDocument(id, rejectNotes);
      notifications.show({ title: 'Rejected', color: 'orange' });
      setRejectOpen(false);
      setRejectNotes('');
      fetchDoc();
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const handleSaveHeader = async () => {
    try {
      const payload = { ...headerFields };
      for (const key of Object.keys(payload)) {
        if (payload[key] === '') payload[key] = null;
      }
      await apiService.updateAccountingDocumentHeader(id, payload);
      notifications.show({ title: 'Saved', message: 'Header fields updated', color: 'green' });
      setDoc(prev => ({ ...prev, ...payload }));
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const updateHeader = (field, value) => {
    setHeaderFields(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveItems = async () => {
    try {
      await apiService.replaceAccountingDocumentItems(id, items);
      notifications.show({ title: 'Saved', message: 'Items updated', color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUploadReceipt = async (itemId, file) => {
    setUploadingReceipt(itemId);
    try {
      await apiService.uploadItemReceipt(itemId, file);
      notifications.show({ title: 'Receipt attached', color: 'green' });
      fetchDoc();
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    } finally {
      setUploadingReceipt(null);
    }
  };

  const handleRemoveReceipt = async (itemId) => {
    try {
      await apiService.removeItemReceipt(itemId);
      notifications.show({ title: 'Receipt removed', color: 'orange' });
      fetchDoc();
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const handleAddTag = async (idx, tag) => {
    const item = items[idx];
    const prevTags = item.tags || [];
    if (prevTags.includes(tag)) return;
    const newTags = [...prevTags, tag];
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, tags: newTags } : it));
    try {
      await apiService.updateItemTags(item.id, newTags);
    } catch (err) {
      setItems(prev => prev.map((it, i) => i === idx ? { ...it, tags: prevTags } : it));
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const handleRemoveTag = async (idx, tag) => {
    const item = items[idx];
    const prevTags = item.tags || [];
    const newTags = prevTags.filter(t => t !== tag);
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, tags: newTags } : it));
    try {
      await apiService.updateItemTags(item.id, newTags);
    } catch (err) {
      setItems(prev => prev.map((it, i) => i === idx ? { ...it, tags: prevTags } : it));
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  if (loading) {
    return (
      <Group
        role="status"
        aria-live="polite"
        aria-label="Loading document"
        justify="center"
        py="xl"
      >
        <Loader />
      </Group>
    );
  }

  if (!doc) {
    return <Alert icon={<IconAlertCircle size={16} aria-hidden="true" />} title="Not found" color="red" role="alert">Document not found.</Alert>;
  }

  const TI = TYPE_ICONS[doc.document_type] || IconFileDescription;
  const isPdf = doc.mimetype === 'application/pdf';
  const isImage = doc.mimetype?.startsWith('image/');

  const headerFieldsConfig = [
    { key: 'counterparty_name', label: 'Counterparty' },
    { key: 'document_date', label: 'Document Date', type: 'date' },
    { key: 'invoice_number', label: 'Invoice #' },
    { key: 'reference_number', label: 'Reference #' },
    { key: 'mwst_number', label: 'MWST Number' },
    { key: 'iban', label: 'IBAN' },
    { key: 'payment_reference', label: 'Payment Ref' }
  ];

  const total = items.reduce((s, i) => s + (Number(i.converted_amount ?? i.total_amount) || 0), 0);
  const vatTotal = items.reduce((s, i) => s + (Number(i.vat_amount) || 0), 0);

  return (
    <Paper p="md">
      <Group mb="lg">
        <ActionIcon
          variant="subtle"
          onClick={() => navigate('/dashboard/accounting')}
          aria-label="Back to accounting documents list"
        >
          <IconArrowLeft size={20} aria-hidden="true" />
        </ActionIcon>
        <span aria-hidden="true"><TI size={24} /></span>
        <Text component="h1" size="xl" fw={700} tt="capitalize">{doc.document_type?.replace(/_/g, ' ')}</Text>
        <Badge color={STATUS_COLORS[doc.processing_status] || 'gray'} size="lg">
          {doc.processing_status?.replace(/_/g, ' ')}
        </Badge>
        {doc.is_approved ? (
          <Badge color="green" leftSection={<IconCheck size={14} />} size="lg">Approved</Badge>
        ) : doc.is_approved === 0 || doc.is_approved === false ? (
          <Badge color="red" leftSection={<IconX size={14} />} size="lg">Rejected</Badge>
        ) : (
          <Badge color="yellow" variant="light" size="lg">Pending Review</Badge>
        )}
        <Text size="xs" c="dimmed" ml="auto">{doc.filename}</Text>
      </Group>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
        {/* Left column: Document preview */}
        <Paper withBorder p={0} style={{ overflow: 'hidden', minHeight: 400 }}>
          {fileLoading ? (
            <Group justify="center" py="xl" style={{ minHeight: 400 }}>
              <Loader />
              <Text size="sm" c="dimmed">Loading document...</Text>
            </Group>
          ) : fileError ? (
            <Group justify="center" py="xl" style={{ minHeight: 400 }} role="alert">
              <IconFileDescription size={48} stroke={1.5} color="var(--mantine-color-gray-4)" aria-hidden="true" />
              <Text size="sm" c="red">{fileError}</Text>
            </Group>
          ) : isPdf && fileUrl ? (
            <iframe
              src={fileUrl}
              title={doc.filename}
              style={{ width: '100%', height: 'calc(100vh - 200px)', border: 'none', minHeight: 500 }}
            />
          ) : isImage && fileUrl ? (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <img
                src={fileUrl}
                alt={doc.filename}
                style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 200px)', objectFit: 'contain' }}
              />
            </div>
          ) : (
            <Group justify="center" py="xl" style={{ minHeight: 400 }} role="status">
              <IconFile size={64} stroke={1.5} color="var(--mantine-color-gray-4)" aria-hidden="true" />
              <Text size="sm" c="dimmed">Preview not available for this file type</Text>
            </Group>
          )}
        </Paper>

        {/* Right column: Extracted data */}
        <Stack gap="md">
          <Group justify="space-between" align="end">
            <Text size="sm" fw={600} tt="uppercase" c="dimmed">Extracted Fields</Text>
            <Button size="xs" variant="light" onClick={handleSaveHeader}>Save Header</Button>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            {headerFieldsConfig.map(field => (
              <TextInput
                key={field.key}
                label={field.label}
                size="xs"
                type={field.type || 'text'}
                value={headerFields[field.key] || ''}
                onChange={e => updateHeader(field.key, e.target.value)}
              />
            ))}
            <Card withBorder padding="sm">
              <Text size="xs" c="dimmed">Uploaded</Text>
              <Text size="sm" fw={500}>{doc.created_at ? new Date(doc.created_at).toLocaleString() : '—'}</Text>
            </Card>
          </SimpleGrid>

          {doc.processing_status === 'pending' && (
            <Alert icon={<IconAlertCircle size={16} aria-hidden="true" />} title="Not Processed">
              This document has not been processed by AI yet.
              <Button leftSection={<IconCpu size={16} aria-hidden="true" />} onClick={handleProcess} loading={processing} ml="md" size="xs">
                Process with AI
              </Button>
            </Alert>
          )}

          {doc.processing_status === 'failed' && (
            <Alert icon={<IconX size={16} aria-hidden="true" />} title="Processing Failed" color="red">
              {doc.error_message || 'AI extraction failed.'}
              <Button leftSection={<IconCpu size={16} aria-hidden="true" />} onClick={handleProcess} loading={processing} ml="md" size="xs" color="red">
                Retry
              </Button>
            </Alert>
          )}

          {doc.processing_status === 'needs_review' && (
            <Alert icon={<IconAlertCircle size={16} aria-hidden="true" />} title="Needs Review" color="orange">
              {doc.error_message || 'AI extraction completed but needs human review.'}
            </Alert>
          )}

          <Group>
            {!doc.is_approved && ['completed', 'needs_review'].includes(doc.processing_status) && (
              <>
                <Button leftSection={<IconCheck size={16} />} color="green" onClick={handleApprove}>Approve</Button>
                <Button leftSection={<IconX size={16} />} color="orange" variant="light" onClick={() => setRejectOpen(true)}>Reject</Button>
              </>
            )}
            {['completed', 'needs_review', 'failed'].includes(doc.processing_status) && (
              <Button leftSection={<IconCpu size={16} />} variant="light" onClick={handleProcess} loading={processing}>
                Reprocess
              </Button>
            )}
          </Group>

          <Divider label="Line Items" labelPosition="center" />

          {items.length === 0 ? (
            <Alert icon={<IconAlertCircle size={16} />} title="No Items">
              {doc.processing_status === 'completed' || doc.processing_status === 'needs_review'
                ? 'No line items were extracted.'
                : 'Process the document to extract line items.'}
            </Alert>
          ) : (
            <>
              <Table.ScrollContainer minWidth={500}>
              <Table striped highlightOnHover withTableBorder>
                <Table.Caption>Extracted line items for document {doc.filename}</Table.Caption>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th scope="col">Description</Table.Th>
                    <Table.Th scope="col" style={{ width: 110 }}>Counterparty</Table.Th>
                    <Table.Th scope="col" style={{ width: 60 }}>Qty</Table.Th>
                    <Table.Th scope="col" style={{ width: 90 }}>Unit Price</Table.Th>
                    <Table.Th scope="col" style={{ width: 100 }}>Total</Table.Th>
                    {['credit_note', 'debit_note', 'bank_statement'].includes(doc.document_type) && (
                      <Table.Th scope="col" style={{ width: 50 }}>Receipt</Table.Th>
                    )}
                    <Table.Th scope="col" style={{ width: 40 }}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {items.map((item, idx) => {
                    const entryType = getEntryType(item, doc.document_type);
                    const showReceipt = ['credit_note', 'debit_note', 'bank_statement'].includes(doc.document_type);
                    return (
                    <Table.Tr key={item.id || idx}>
                      <Table.Td>
                        <Stack gap={4}>
                          <TextInput size="xs" value={item.description || ''} onChange={e => updateItem(idx, 'description', e.target.value)} variant="unstyled" />
                          <Group gap={4}>
                            {(item.tags || []).map(tag => (
                              <Badge
                                key={tag}
                                size="xs"
                                color={TAG_COLORS[tag] || 'gray'}
                                variant="light"
                                rightSection={
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveTag(idx, tag)}
                                    aria-label={`Remove tag ${tag}`}
                                    style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center' }}
                                  >
                                    <IconX size={10} aria-hidden="true" />
                                  </button>
                                }
                              >
                                {tag}
                              </Badge>
                            ))}
                            <Menu withinPortal>
                              <Menu.Target>
                                <ActionIcon size="xs" variant="subtle" color="gray" aria-label={`Add tag to line item ${idx + 1}`}>
                                  <IconPlus size={12} aria-hidden="true" />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                {TAG_OPTIONS.filter(t => !(item.tags || []).includes(t.value)).map(t => (
                                  <Menu.Item key={t.value} leftSection={<IconPlus size={12} />} onClick={() => handleAddTag(idx, t.value)}>
                                    {t.label}
                                  </Menu.Item>
                                ))}
                                {TAG_OPTIONS.every(t => (item.tags || []).includes(t.value)) && (
                                  <Menu.Item disabled>All tags added</Menu.Item>
                                )}
                              </Menu.Dropdown>
                            </Menu>
                          </Group>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <TextInput size="xs" value={item.counterparty_name || ''} onChange={e => updateItem(idx, 'counterparty_name', e.target.value || null)} variant="unstyled" placeholder="—" />
                      </Table.Td>
                      <Table.Td>
                        <NumberInput size="xs" value={item.quantity} onChange={v => updateItem(idx, 'quantity', v)} decimalScale={3} min={0} variant="unstyled" styles={{ input: { width: 50 } }} />
                      </Table.Td>
                      <Table.Td>
                        <NumberInput size="xs" value={item.unit_price} onChange={v => updateItem(idx, 'unit_price', v)} decimalScale={2} min={0} variant="unstyled" aria-label={`Unit price for line ${idx + 1}`} leftSection={<IconCurrencyFrank size={12} aria-hidden="true" />} styles={{ input: { width: 75 } }} />
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={2}>
                          <Text size="sm" fw={500} c={entryType === 'expense' ? 'red' : undefined}>
                            {(item.original_currency || 'CHF')} {Number(item.quantity != null && item.unit_price != null ? (item.quantity * item.unit_price) : Number(item.total_amount || 0)).toFixed(2)}
                          </Text>
                          {item.original_currency && item.original_currency !== 'CHF' && item.converted_amount != null && (
                            <Text size="xs" c="dimmed">CHF {Number(item.converted_amount).toFixed(2)}</Text>
                          )}
                        </Stack>
                      </Table.Td>
                      {showReceipt && (
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          {item.receipt_file_id ? (
                            <Tooltip label="Remove receipt" color="orange">
                              <ActionIcon
                                variant="light"
                                color="green"
                                size="sm"
                                aria-label={`Remove linked receipt for line ${idx + 1}`}
                                onClick={() => {
                                  const confirmed = window.confirm('Remove the linked receipt from this item?');
                                  if (confirmed) handleRemoveReceipt(item.id);
                                }}
                              >
                                <IconPaperclip size={14} aria-hidden="true" />
                              </ActionIcon>
                            </Tooltip>
                          ) : (
                            <>
                              <Tooltip label="Attach receipt">
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  size="sm"
                                  loading={uploadingReceipt === item.id}
                                  aria-label={`Attach receipt to line ${idx + 1}`}
                                  onClick={() => { const el = document.getElementById(`receipt-${item.id}`); if (el) el.click(); }}
                                >
                                  <IconUpload size={14} aria-hidden="true" />
                                </ActionIcon>
                              </Tooltip>
                              <input
                                id={`receipt-${item.id}`}
                                type="file"
                                accept="image/jpeg,image/png,image/webp,application/pdf"
                                style={{ display: 'none' }}
                                aria-label={`Receipt file upload for line ${idx + 1}`}
                                onChange={e => {
                                  if (e.target.files[0]) handleUploadReceipt(item.id, e.target.files[0]);
                                  e.target.value = '';
                                }}
                              />
                            </>
                          )}
                        </Group>
                      </Table.Td>
                      )}
                      <Table.Td>
                        <ActionIcon
                          variant="light"
                          color="red"
                          size="sm"
                          aria-label={`Remove line item ${idx + 1}`}
                          onClick={() => removeItem(idx)}
                        >
                          <IconTrash size={14} aria-hidden="true" />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
              </Table.ScrollContainer>

              <Group>
                <Text size="sm" fw={500}>Total: {formatPrice(total)}</Text>
                {vatTotal > 0 && (
                  <>
                    <Text size="sm" c="dimmed">|</Text>
                    <Text size="sm" fw={500}>VAT: {formatPrice(vatTotal)}</Text>
                  </>
                )}
                <Button size="xs" variant="light" onClick={handleSaveItems} ml="auto">Save Changes</Button>
              </Group>
            </>
          )}

          {doc.gemini_raw_response && (
            <>
              <Divider label="AI Raw Response" labelPosition="center" />
              <Group>
                <Button
                  variant="light"
                  size="xs"
                  leftSection={showRaw ? <IconEyeOff size={14} aria-hidden="true" /> : <IconEye size={14} aria-hidden="true" />}
                  aria-expanded={showRaw}
                  aria-controls="ai-raw-response-region"
                  onClick={() => setShowRaw(!showRaw)}
                >
                  {showRaw ? 'Hide' : 'Show'} Raw Response
                </Button>
              </Group>
              <Collapse in={showRaw}>
                <Paper
                  id="ai-raw-response-region"
                  withBorder
                  p="sm"
                  style={{ maxHeight: 300, overflow: 'auto' }}
                  role="region"
                  aria-label="AI raw response"
                >
                  <Text size="xs" component="pre" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                    {doc.gemini_raw_response}
                  </Text>
                </Paper>
              </Collapse>
            </>
          )}
        </Stack>
      </SimpleGrid>

      <Modal opened={!!approveWarning} onClose={() => setApproveWarning(null)} title="Approval Warning" size="md">
        <Text size="sm" mb="md">{approveWarning?.warning}</Text>
        <Text size="sm" c="dimmed" mb="md">The AI-extracted total may be inaccurate. Proceed with approval anyway?</Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={() => setApproveWarning(null)}>Cancel</Button>
          <Button color="green" onClick={handleForceApprove}>Approve anyway</Button>
        </Group>
      </Modal>

      <Modal opened={rejectOpen} onClose={() => { setRejectOpen(false); setRejectNotes(''); }} title="Reject Document" size="sm">
        <Stack>
          <TextInput label="Notes (optional)" placeholder="Why is this being rejected?" value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} />
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button color="orange" onClick={handleReject}>Reject</Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
